<template>
  <div v-if="overview">
    <span
      v-for="(token, i) in flattened"
      :key="i"
      :class="['token', token.type]"
      @click="tokenClick(token)"
      >{{ token.value }}</span
    >
  </div>
</template>

<script setup>
import { getRuleOverview } from '@/api';
import { ref, watch, computed } from 'vue';
import { useRouter } from 'vue-router/composables';

const props = defineProps({
  version: Object,
  ruleId: Number,

  // Update this number to trigger a refresh
  refreshCounter: Number,
});

const isLoading = ref(false);
const overview = ref(null);

const router = useRouter();

function flattenOverview(tokenOrGroup) {
  if (tokenOrGroup.type !== 'group') {
    return [tokenOrGroup];
  }

  const out = [];
  for (const child of tokenOrGroup.tokens) {
    out.push(...flattenOverview(child));
  }

  return out;
}

function tokenClick(token) {
  const meta = token.metadata;
  if (meta.question_id && meta.section_id) {
    router.push({
      name: 'questions',
      params: {
        versionId: props.version.id,
        sectionId: meta.section_id,
        questionId: meta.question_id,
      },
    });
    return;
  }

  if (meta.rule_id && meta.section_id) {
    router.push({
      name: 'rules',
      params: {
        versionId: props.version.id,
        sectionId: meta.section_id,
        ruleId: meta.rule_id,
      },
    });
    return;
  }
}

const flattened = computed(() =>
  overview.value ? flattenOverview(overview.value) : null
);

async function loadOverview() {
  isLoading.value = true;
  overview.value = (await getRuleOverview(props.version, props.ruleId)).data;
  isLoading.value = false;
}

watch(
  () => [props.version.id, props.ruleId, props.refreshCounter],
  () => loadOverview(),
  {
    immediate: true,
    flush: 'post',
  }
);
</script>

<style scoped lang="scss">
.functional {
  color: #0f766e;
  font-weight: 600;
}

.operator {
  color: #0f172a;
  font-weight: 700;
}

.syntax {
  color: #94a3b8;
  font-weight: 400;
}

.spacing {
  color: #cbd5e1;
  font-weight: 400;
}

.value {
  color: #dc2626;
  font-weight: 500;
}

.external-input {
  color: #d97706;
  font-weight: 500;
}

.rule-code {
  color: #0284c7;
  font-weight: 500;
}

.question-code {
  color: #1d4ed8;
  font-weight: 500;
}

.option-code {
  color: #be185d;
  font-weight: 500;
}

.question-code,
.rule-code,
.option-code,
.external-input {
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
}
</style>
